// frontend/src/components/librarian/dashboard/dashboard-format.ts
// Copy and number helpers for the staff dashboard (shared by both shells).
import { format, parseISO } from 'date-fns';
import type { DashboardStatistics } from '@/services/dashboard-api';

/** "Good morning" before noon, "Good afternoon" until 5 pm, then "Good evening". */
export const greetingFor = (d: Date = new Date()) => {
  const h = d.getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

const count = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

const joinList = (parts: string[]) =>
  parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

/** One sentence on what needs the desk today, from the live counters. */
export const todayLine = (s?: DashboardStatistics) => {
  if (!s) return 'Here’s what’s happening in the library today.';
  const parts: string[] = [];
  if (s.requests.pending > 0) parts.push(`${count(s.requests.pending, 'request is', 'requests are')} waiting for approval`);
  if (s.loans.due_today > 0) parts.push(`${count(s.loans.due_today, 'loan is', 'loans are')} due back today`);
  if (s.loans.overdue > 0) parts.push(`${count(s.loans.overdue, 'loan is', 'loans are')} overdue`);
  if (parts.length === 0) return 'Nothing is waiting for the desk today.';
  const line = joinList(parts);
  return `${line[0].toUpperCase()}${line.slice(1)}.`;
};

/** "$12.50", as the Penalties page prints fines. */
export const money = (v: number | string | null | undefined) => `$${Number(v ?? 0).toFixed(2)}`;

/** "1 Sep" from "2026-09-01" for chart ticks. */
export const shortDay = (iso: string) => format(parseISO(iso), 'd MMM');

/** "38% of books", or an em dash while the total is unknown. */
export const shareOfBooks = (part?: number, total?: number) =>
  part !== undefined && total ? `${Math.round((part / total) * 100)}% of books` : '—';

export const QUOTES: ReadonlyArray<{ text: string; by: string }> = [
  { text: 'A room without books is like a body without a soul.', by: 'Cicero' },
  { text: 'I have always imagined that Paradise will be a kind of library.', by: 'Jorge Luis Borges' },
  { text: 'A library is not a luxury but one of the necessities of life.', by: 'Henry Ward Beecher' },
  { text: 'Reading is to the mind what exercise is to the body.', by: 'Joseph Addison' },
  { text: 'A book is a dream that you hold in your hand.', by: 'Neil Gaiman' },
  { text: 'Books are a uniquely portable magic.', by: 'Stephen King' },
];

/** The same quote all day, a different one tomorrow. */
export const quoteForDay = (d: Date = new Date()) => {
  const start = new Date(d.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((d.getTime() - start.getTime()) / 86_400_000);
  return QUOTES[dayOfYear % QUOTES.length];
};
